// ===================================
// KAIXO - Canales V2 Modal (Beta)
// Componente aislado, no toca los canales v1.
// ===================================

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './ChannelsV2.css';
import { Icons } from '../Icons/Icons';
import ct, { BUSINESS_CATEGORIES } from './i18n';
import { pickId } from './utils';
import { getSocket } from '../../services/socket';
import {
    v2CreateChannel, v2GetUserChannels, v2GetChannel,
    v2Subscribe, v2Unsubscribe,
    v2PublishText, v2PublishImage, v2PublishCarousel, v2GetMessages, v2GetFeed,
    v2DeleteChannel, v2SendInquiry, v2UploadAvatar, v2UpdateChannel,
    v2ReactToMessage, v2AddComment, v2GetComments, v2DeleteComment, v2GetByHashtag, v2SearchChannels, v2IncrementShare,
    v2GetMessageById, v2UpdateMessage, v2DeleteMessage, v2TranslateComment, v2LikeComment,
    v2GetMyFollowers
} from '../../services/api';
import ChannelProfile from './ChannelProfile';
import InviteFollowersModal from './InviteFollowersModal';
import StoriesBar from './StoriesBar';
import PublishModal from './PublishModal';
import FollowersList from './FollowersList';
import OwnChannelsList from './OwnChannelsList';
import FollowedChannelsList from './FollowedChannelsList';
import HashtagView from './HashtagView';
import FeedItem from './FeedItem';
import ChatMessageItem from './ChatMessageItem';
import ImageViewer from '../ImageViewer/ImageViewer';
import VideoViewer from '../ImageViewer/VideoViewer';
import BoostCreateModal from '../Boost/BoostCreateModal';
import LeaderboardModal from '../LatidoDorado/LeaderboardModal';
import LeaderboardIcon from '../LatidoDorado/LeaderboardIcon';
import { getInterestLabel } from '../../constants/interests';
import { COUNTRIES_LIST, getRegionsForCountry, nativeName } from '../../constants/countries';
import InterestPickerModal from '../InterestPicker/InterestPickerModal';
import useModalClose from '../../hooks/useModalClose';

const LANGUAGE_OPTIONS = [
    'español', 'inglés', 'francés', 'alemán', 'portugués', 'italiano',
    'euskera', 'chino', 'japonés', 'árabe', 'hindi', 'ruso',
    'coreano', 'turco', 'indonesio'
];

function ChannelsV2Modal({ currentUser, onClose, embedded = false }) {
    // En modo embedded el AdminPanel u otro contenedor padre maneja su propio cierre.
    useModalClose(onClose, !embedded);
    const [view, setView] = useState('home'); // home | list | chat | createStep1 | createStep2 | subscribe
    const [channels, setChannels] = useState([]);
    const [feed, setFeed] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Crear canal (wizard)
    const [newType, setNewType] = useState(null); // 'personal' | 'business'
    const [newName, setNewName] = useState('');
    const [newDesc, setNewDesc] = useState('');
    const [newCategory, setNewCategory] = useState('');
    const [newBio, setNewBio] = useState('');
    const [newLinks, setNewLinks] = useState('');
    const [newIsPublic, setNewIsPublic] = useState(false);
    const [newContactMode, setNewContactMode] = useState('direct');
    const [newTargetCountries, setNewTargetCountries] = useState([]);
    const [newTargetRegions, setNewTargetRegions] = useState([]);
    const [newTargetLanguages, setNewTargetLanguages] = useState([]);
    const [newTargetInterests, setNewTargetInterests] = useState([]);
    const [newAvatarFile, setNewAvatarFile] = useState(null);
    const [newAvatarPreview, setNewAvatarPreview] = useState(null);
    const avatarInputRef = useRef(null);
    const [editingChannelId, setEditingChannelId] = useState(null);
    const [commentsEnabled, setCommentsEnabled] = useState(true);
    const [postInterests, setPostInterests] = useState([]); // 3-5 intereses del latido
    const [showInterestPickerFor, setShowInterestPickerFor] = useState(null); // 'newTarget' | 'post' | 'editPost'
    const [commentsFor, setCommentsFor] = useState(null); // {messageId, comments[]} — modal legacy (sigue disponible en chat)
    const [commentText, setCommentText] = useState('');
    // Inline comments expansion per message in the feed
    const [expandedComments, setExpandedComments] = useState({}); // { [msgId]: comments[] | 'loading' }
    const [inlineDraft, setInlineDraft] = useState({}); // { [msgId]: text }
    // Traducciones bajo demanda y likes de comentarios
    const [commentTranslations, setCommentTranslations] = useState({}); // { [commentId]: { text, language, loading, error } }
    const [commentLikes, setCommentLikes] = useState({}); // { [commentId]: { likesCount, likedByMe } }
    // Filtro del feed home: 'following' (canales seguidos), 'own' (latidos propios)
    // Modal de invitar a seguir (solo owner). Guarda el canal para el que se abre.
    const [showInviteFor, setShowInviteFor] = useState(null);
    const [followers, setFollowers] = useState([]);
    const [followersLoading, setFollowersLoading] = useState(false);
    const [reactionPicker, setReactionPicker] = useState(null);
    const [fullImage, setFullImage] = useState(null);
    const [fullVideo, setFullVideo] = useState(null);
    // Modal dedicado de publicación (reemplaza el formulario inline del chat)
    const [publishModal, setPublishModal] = useState(null);  // { channel, mode }
    const [hashtagView, setHashtagView] = useState(null); // { tag, messages, loading }
    const [boostFor, setBoostFor] = useState(null); // message seleccionado para impulsar
    const [showLeaderboard, setShowLeaderboard] = useState(false);
    const [editingPost, setEditingPost] = useState(null); // {_id, originalText, visibility, contactMode, commentsEnabled, interests}
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const searchTimerRef = useRef(null);

    // Publicar — override de contacto por mensaje
    // '' = hereda del canal; si se cambia, overridea SOLO este post
    const [overrideContactMode, setOverrideContactMode] = useState('');

    // Imagen pendiente de publicar (preview antes de enviar)
    const [pendingImage, setPendingImage] = useState(null);
    const [pendingImagePreview, setPendingImagePreview] = useState(null);
    // Carrusel: array de {file, previewUrl, type:'image'|'video'} de hasta 10 medios.
    // Coexiste con pendingImage (single, retrocompat). Si hay pendingMedia.length>0,
    // se usa carrusel al publicar; si no y hay pendingImage, se usa endpoint legacy.
    const [pendingMedia, setPendingMedia] = useState([]);

    // Formulario de consulta
    const [inquiryFor, setInquiryFor] = useState(null);
    const [inquiryName, setInquiryName] = useState('');
    const [inquiryMsg, setInquiryMsg] = useState('');
    const [inquiryEmail, setInquiryEmail] = useState('');
    const [inquirySuccess, setInquirySuccess] = useState(false);

    // Perfil público
    const [profileCode, setProfileCode] = useState(null);

    // Suscribirse

    // Ver canal
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [visibility, setVisibility] = useState('subscribers');
    // Historia 24h: si está activo, el latido no aparece en el feed, solo
    // en la barra de historias arriba, y se borra automáticamente en 24h.
    const [isStory, setIsStory] = useState(false);
    const [uploading, setUploading] = useState(false);
    const imageInputRef = useRef(null);
    const feedEndRef = useRef(null);
    const addedMsgIds = useRef(new Set());

    // Cleanup de objectURLs al desmontar
    useEffect(() => {
        return () => {
            if (newAvatarPreview?.startsWith('blob:')) URL.revokeObjectURL(newAvatarPreview);
            if (pendingImagePreview?.startsWith('blob:')) URL.revokeObjectURL(pendingImagePreview);
        };
    }, []); // eslint-disable-line

    // Caché stale-while-revalidate: en arranques posteriores mostramos
    // el feed/canales del último uso INMEDIATAMENTE, y refrescamos en
    // background. TTL implícito: la caché es solo válida para el mismo
    // userId — al cambiar de usuario se ignora.
    const cacheKey = currentUser?._id ? `kaixo_v2_feed_cache_${currentUser._id}` : null;
    const tryReadCache = () => {
        if (!cacheKey || typeof localStorage === 'undefined') return null;
        try {
            const raw = localStorage.getItem(cacheKey);
            if (!raw) return null;
            const obj = JSON.parse(raw);
            if (!obj || !Array.isArray(obj.channels) || !Array.isArray(obj.feed)) return null;
            // Si la caché tiene >24h, la descartamos (datos demasiado viejos)
            if (obj.savedAt && Date.now() - obj.savedAt > 24 * 60 * 60 * 1000) return null;
            return obj;
        } catch { return null; }
    };
    const writeCache = (channels, feed) => {
        if (!cacheKey || typeof localStorage === 'undefined') return;
        try {
            // Recortamos para no llenar localStorage: máx 30 posts del feed
            // y todos los canales (que son pocos).
            localStorage.setItem(cacheKey, JSON.stringify({
                channels,
                feed: feed.slice(0, 30),
                savedAt: Date.now()
            }));
        } catch { /* localStorage lleno o deshabilitado */ }
    };

    const loadEverything = async () => {
        if (!currentUser?._id) return;
        // Hidratación instantánea desde caché si la hay — sin spinner
        const cached = tryReadCache();
        if (cached) {
            setChannels(cached.channels);
            setFeed(cached.feed);
            setLoading(false);
        } else {
            setLoading(true);
        }
        try {
            const [ch, fd] = await Promise.all([
                v2GetUserChannels(currentUser._id),
                v2GetFeed({ userId: currentUser._id })
            ]);
            const freshChannels = Array.isArray(ch.data?.channels) ? ch.data.channels : [];
            const freshFeed = Array.isArray(fd.data?.messages) ? fd.data.messages : [];
            setChannels(freshChannels);
            setFeed(freshFeed);
            writeCache(freshChannels, freshFeed);
            // Limitar addedMsgIds para que no crezca infinito
            if (addedMsgIds.current.size > 500) {
                const arr = Array.from(addedMsgIds.current);
                addedMsgIds.current = new Set(arr.slice(-200));
            }
        } catch (e) {
            // Solo mostramos error si NO había caché; con caché, el usuario
            // ve algo y la red volverá a intentarlo en el siguiente refresh.
            if (!cached) setError(e.response?.data?.error || e.message);
        }
        setLoading(false);
    };

    useEffect(() => { loadEverything(); }, [currentUser?._id]); // eslint-disable-line

    // Escuchar evento externo para refrescar la lista/feed (p.ej. tras auto-subscribe)
    useEffect(() => {
        const refresh = () => loadEverything();
        window.addEventListener('v2_channels_updated', refresh);
        return () => window.removeEventListener('v2_channels_updated', refresh);
    }, []); // eslint-disable-line

    // Socket en tiempo real para mensajes V2
    useEffect(() => {
        const socket = typeof getSocket === 'function' ? getSocket() : null;
        if (!socket) return undefined;
        const handler = (msg) => {
            const id = String(msg.messageId || msg._id);
            if (addedMsgIds.current.has(id)) return;
            addedMsgIds.current.add(id);

            // En vista chat de SU canal → añadir al final
            if (selectedChannel && String(msg.channelId) === String(selectedChannel._id)) {
                setMessages(prev => [...prev, msg]);
            }

            // Feed home: el payload del socket YA incluye channelName,
            // channelAvatar, channelInviteCode, mediaUrls[], etc. (fix
            // backend). Solo fallback al state local si algún campo
            // faltara (socket viejo o compat).
            setFeed(prev => {
                if (prev.some(m => String(m._id) === id)) return prev;
                const channelRef = String(msg.channelId || msg.channel || '');
                const ch = !msg.channelName && channels.find(c => String(c._id) === channelRef);
                const enriched = {
                    ...msg,
                    _id: msg._id || msg.messageId,
                    channel: msg.channel || channelRef,
                    channelName: msg.channelName || ch?.name,
                    channelAvatar: msg.channelAvatar || ch?.avatar,
                    channelType: msg.channelType || ch?.type,
                    channelHandle: msg.channelHandle || ch?.handle,
                    channelOwner: msg.channelOwner || ch?.owner?._id || ch?.owner,
                    channelInviteCode: msg.channelInviteCode || ch?.inviteCode,
                    effectiveContactMode: msg.contactMode || ch?.contactMode || 'none'
                };
                // Solo si tenemos al menos channelName → renderizable.
                if (!enriched.channelName) return prev;
                return [enriched, ...prev];
            });
        };
        socket.off('channel_v2_message', handler);
        socket.on('channel_v2_message', handler);
        return () => {
            const s = typeof getSocket === 'function' ? getSocket() : null;
            if (s) s.off('channel_v2_message', handler);
        };
    }, [selectedChannel, channels]); // eslint-disable-line

    useEffect(() => {
        if (feedEndRef.current && view === 'chat') {
            feedEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, view]);

    // Búsqueda de canales con debounce
    useEffect(() => {
        clearTimeout(searchTimerRef.current);
        if (!searchQuery || searchQuery.trim().length < 2) {
            setSearchResults([]);
            setSearchLoading(false);
            return;
        }
        setSearchLoading(true);
        searchTimerRef.current = setTimeout(async () => {
            try {
                const { data } = await v2SearchChannels(searchQuery.trim());
                setSearchResults(data.channels || []);
            } catch { setSearchResults([]); }
            setSearchLoading(false);
        }, 300);
        return () => clearTimeout(searchTimerRef.current);
    }, [searchQuery]);

    // Carga la lista de seguidores cuando se abre esa vista
    useEffect(() => {
        if (view !== 'followers') return;
        let cancelled = false;
        setFollowersLoading(true);
        (async () => {
            try {
                const { data } = await v2GetMyFollowers();
                if (!cancelled) setFollowers(data?.followers || []);
            } catch {
                if (!cancelled) setFollowers([]);
            } finally {
                if (!cancelled) setFollowersLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [view]);

    // Listener: desde el perfil del canal propio, al tocar 'Publicar latido'
    // o 'Publicar historia' se dispara este evento. Abrimos el modal
    // dedicado con el modo correcto (cada modo muestra sus propios campos).
    useEffect(() => {
        const handler = async (e) => {
            const { channelId, mode } = e.detail || {};
            if (!channelId) return;
            try {
                const { data } = await v2GetChannel(channelId);
                const ch = data?.channel;
                if (!ch) return;
                setPublishModal({ channel: ch, mode: mode === 'story' ? 'story' : 'latido' });
            } catch {}
        };
        window.addEventListener('open_publish_modal', handler);
        return () => window.removeEventListener('open_publish_modal', handler);
    }, []);

    // Escucha eventos del perfil del canal propio: editar / impulsar
    // los posts del owner directamente desde la vista perfil.
    useEffect(() => {
        const onEdit = (e) => {
            const p = e.detail;
            if (!p?._id) return;
            setProfileCode(null);
            setEditingPost(p);
        };
        const onBoost = async (e) => {
            const { message, channelId } = e.detail || {};
            if (!message) return;
            setProfileCode(null);
            // Para BoostCreateModal, hace falta 'selectedChannel'. Si no
            // lo tenemos aún, lo cargamos rápido.
            if (channelId && (!selectedChannel || String(selectedChannel._id) !== String(channelId))) {
                try {
                    const { data } = await v2GetChannel(channelId);
                    if (data?.channel) setSelectedChannel(data.channel);
                } catch {}
            }
            setBoostFor(message);
        };
        const onDelete = async (e) => {
            const id = e.detail?._id;
            if (!id) return;
            const confirmDelete = window.confirm('¿Eliminar este latido? Esta acción no se puede deshacer.');
            if (!confirmDelete) return;
            try {
                await v2DeleteMessage(id);
                // Quitar del feed local sin esperar refetch — UX inmediata
                setFeed(prev => prev.filter(m => String(m._id) !== String(id)));
                window.dispatchEvent(new CustomEvent('v2_channels_updated'));
            } catch (err) {
                setError(err?.response?.data?.error || err?.message || 'No se pudo eliminar el latido');
            }
        };
        window.addEventListener('v2_edit_post', onEdit);
        window.addEventListener('v2_boost_post', onBoost);
        window.addEventListener('v2_delete_post', onDelete);
        return () => {
            window.removeEventListener('v2_edit_post', onEdit);
            window.removeEventListener('v2_boost_post', onBoost);
            window.removeEventListener('v2_delete_post', onDelete);
        };
    }, [selectedChannel]);

    // Listener para abrir un post específico compartido (/p/:messageId)
    useEffect(() => {
        const handler = async (e) => {
            const messageId = e.detail?.messageId;
            if (!messageId) return;
            try {
                const { data } = await v2GetMessageById(messageId);
                if (data?.message) {
                    setFeed(prev => {
                        const exists = prev.some(m => String(m._id) === String(messageId));
                        return exists ? prev : [data.message, ...prev];
                    });
                    setView('home');
                    // Scroll hacia arriba y destacar
                    setTimeout(() => {
                        const el = document.querySelector(`[data-post-id="${messageId}"]`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 200);
                }
            } catch (err) {
                console.error('open_post:', err);
            }
        };
        window.addEventListener('open_post', handler);
        return () => window.removeEventListener('open_post', handler);
    }, []);

    // Listener para abrir vista de hashtag al pulsar un #tag en cualquier mensaje
    useEffect(() => {
        const handler = async (e) => {
            const tag = e.detail?.tag;
            if (!tag) return;
            setHashtagView({ tag, messages: [], loading: true });
            try {
                const { data } = await v2GetByHashtag(tag);
                setHashtagView({ tag, messages: data.messages || [], loading: false });
            } catch (err) {
                setHashtagView({ tag, messages: [], loading: false, error: err.message });
            }
        };
        window.addEventListener('open_hashtag', handler);
        return () => window.removeEventListener('open_hashtag', handler);
    }, []);

    const openChannel = async (ch) => {
        try {
            const [chRes, msgRes] = await Promise.all([
                v2GetChannel(ch._id),
                v2GetMessages(ch._id, { userId: currentUser._id, limit: 50 })
            ]);
            setSelectedChannel(chRes.data.channel);
            setMessages(msgRes.data.messages || []);
            setView('chat');
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const resetCreateForm = () => {
        setNewType(null); setNewName(''); setNewDesc(''); setNewCategory('');
        setNewBio(''); setNewLinks(''); setNewIsPublic(false);
        setNewTargetCountries([]); setNewTargetRegions([]);
        setNewTargetLanguages([]); setNewTargetInterests([]);
        setNewAvatarFile(null); setNewAvatarPreview(null);
        setEditingChannelId(null);
    };

    const handleCreateOrUpdate = async () => {
        if (!newName.trim()) { setError(ct.name + ' ' + (ct.error || 'required')); return; }
        try {
            // NUNCA guardar blob URLs (solo valen en la sesión actual del navegador)
            let avatarUrl = (newAvatarPreview && !String(newAvatarPreview).startsWith('blob:'))
                ? newAvatarPreview
                : '';
            if (newAvatarFile) {
                try {
                    const fd = new FormData();
                    fd.append('avatar', newAvatarFile);
                    fd.append('userId', currentUser._id);
                    const { data: upData } = await v2UploadAvatar(fd);
                    if (upData?.url) avatarUrl = upData.url;
                    else throw new Error('Upload response sin url');
                } catch (e) {
                    setError('No se pudo subir el avatar: ' + (e.response?.data?.error || e.message));
                    return; // abortar el guardado si hay foto pendiente pero falla
                }
            }
            const linksArray = newLinks.split('\n').map(s => s.trim()).filter(Boolean)
                // eslint-disable-next-line no-script-url -- seguridad: sanitizamos posibles javascript:URLs
                .filter(l => !l.startsWith('javascript:') && !l.startsWith('data:'))
                .slice(0, 5);
            const payload = {
                ownerId: currentUser._id,
                name: newName.trim(),
                description: newDesc.trim(),
                avatar: avatarUrl,
                category: newCategory.trim(),
                bio: newBio.trim(),
                links: linksArray,
                isPublic: newType === 'business' ? newIsPublic : false,
                contactMode: newContactMode,
                targetCountries: newTargetCountries,
                targetRegions: newTargetRegions,
                targetLanguages: newTargetLanguages,
                targetInterests: newTargetInterests
            };

            if (editingChannelId) {
                await v2UpdateChannel(editingChannelId, payload);
            } else {
                payload.type = newType;
                payload.contactMode = newContactMode;
                const { data } = await v2CreateChannel(payload);
                setChannels(prev => [data.channel, ...prev]);
            }

            resetCreateForm();
            await loadEverything();
            setView('list');
            setError('');
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const handlePublish = async () => {
        if (!selectedChannel) return;
        if (!text.trim() && !pendingImage && pendingMedia.length === 0) return;

        // Validación 3-5 intereses para canales business
        if (selectedChannel?.type === 'business' && (postInterests.length < 3 || postInterests.length > 5)) {
            setError(ct.interestsCountError || 'Selecciona entre 3 y 5 intereses para tu latido');
            return;
        }

        try {
            let response;
            if (pendingMedia.length > 0) {
                // Carrusel: 1 o más medios (fotos + videos cortos).
                setUploading(true);
                const fd = new FormData();
                pendingMedia.forEach(m => fd.append('media', m.file));
                fd.append('senderId', currentUser._id);
                fd.append('caption', text.trim());
                fd.append('visibility', visibility);
                fd.append('contactMode', overrideContactMode);
                fd.append('commentsEnabled', String(!!commentsEnabled));
                fd.append('interests', JSON.stringify(postInterests));
                fd.append('isStory', String(!!isStory));
                response = await v2PublishCarousel(selectedChannel._id, fd);
                clearPendingMedia();
            } else if (pendingImage) {
                setUploading(true);
                const fd = new FormData();
                fd.append('image', pendingImage);
                fd.append('senderId', currentUser._id);
                fd.append('caption', text.trim());
                fd.append('visibility', visibility);
                fd.append('contactMode', overrideContactMode);
                fd.append('commentsEnabled', String(!!commentsEnabled));
                fd.append('interests', JSON.stringify(postInterests));
                response = await v2PublishImage(selectedChannel._id, fd);
                clearPendingImage();
            } else {
                response = await v2PublishText(selectedChannel._id, {
                    senderId: currentUser._id,
                    text: text.trim(),
                    visibility,
                    contactMode: overrideContactMode,
                    commentsEnabled,
                    interests: postInterests
                });
            }
            setText('');
            setPostInterests([]);
            setIsStory(false);

            const newMsg = response?.data?.message;
            if (newMsg) {
                const id = String(newMsg._id);
                if (!addedMsgIds.current.has(id)) {
                    addedMsgIds.current.add(id);
                    const baseMsg = {
                        ...newMsg,
                        sender: { _id: currentUser._id, name: currentUser.name, avatar: currentUser.avatar },
                        text: newMsg.originalText,
                        effectiveContactMode: newMsg.contactMode || selectedChannel.contactMode || 'none'
                    };
                    setMessages(prev => [...prev, baseMsg]);
                    // También añadir al feed home para que sea visible al volver
                    setFeed(prev => [{
                        ...baseMsg,
                        channelName: selectedChannel.name,
                        channelAvatar: selectedChannel.avatar,
                        channelType: selectedChannel.type,
                        channelOwner: selectedChannel.owner?._id || selectedChannel.owner
                    }, ...prev]);
                }
            }
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        } finally {
            setUploading(false);
        }
    };

    const clearPendingImage = () => {
        if (pendingImagePreview?.startsWith('blob:')) URL.revokeObjectURL(pendingImagePreview);
        setPendingImage(null);
        setPendingImagePreview(null);
    };

    // Carrusel: selección múltiple de fotos+videos (max 10).
    const handleMediaPick = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length || !selectedChannel) return;
        const merged = [...pendingMedia];
        for (const file of files) {
            if (merged.length >= 10) break;
            const isVideo = (file.type || '').startsWith('video/');
            merged.push({
                file,
                previewUrl: URL.createObjectURL(file),
                type: isVideo ? 'video' : 'image'
            });
        }
        setPendingMedia(merged);
        e.target.value = '';
    };

    const removePendingMedia = (idx) => {
        const item = pendingMedia[idx];
        if (item?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(item.previewUrl);
        setPendingMedia(pendingMedia.filter((_, i) => i !== idx));
    };

    const clearPendingMedia = () => {
        pendingMedia.forEach(m => {
            if (m.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(m.previewUrl);
        });
        setPendingMedia([]);
    };

    const handleUnsubscribe = async () => {
        if (!selectedChannel) return;
        if (!window.confirm(ct.confirmLeave)) return;
        try {
            await v2Unsubscribe(selectedChannel._id, { userId: currentUser._id });
            setMessages([]); setInquiryFor(null); setSelectedChannel(null);
            setView('list');
            await loadEverything();
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const handleDelete = async () => {
        if (!selectedChannel) return;
        if (!window.confirm(ct.confirmDelete)) return;
        try {
            await v2DeleteChannel(selectedChannel._id, { userId: currentUser._id });
            setMessages([]); setInquiryFor(null); setSelectedChannel(null);
            setView('list');
            await loadEverything();
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    // ── Contacto ──
    const handleChatWithAdmin = (ownerId) => {
        if (!ownerId) return;
        onClose();
        // Esperar un frame a que el modal se desmonte antes de disparar el chat,
        // para evitar que el modal quede tapando la vista del chat.
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('open_chat_with_user', { detail: { userId: ownerId } }));
        }, 50);
    };

    const openInquiry = (msg, channelIdForInquiry) => {
        setInquiryFor({ ...msg, _channelIdForInquiry: channelIdForInquiry });
        setInquiryName(currentUser.name || '');
        setInquiryMsg('');
        setInquiryEmail(currentUser.email || '');
        setInquirySuccess(false);
    };

    const submitInquiry = async () => {
        if (!inquiryFor || !inquiryMsg.trim()) return;
        // pickId importado desde utils — extrae el id plano aunque venga
        // como objeto entero o como literal "[object Object]".
        const channelIdForInquiry =
            pickId(inquiryFor._channelIdForInquiry)
            || pickId(inquiryFor.channel)
            || pickId(selectedChannel);
        const messageId = pickId(inquiryFor._id) || pickId(inquiryFor.messageId);
        if (!channelIdForInquiry || !messageId) {
            // Log detallado para diagnóstico: si volvemos a ver este error,
            // tenemos los valores crudos para saber desde dónde vinieron.
            console.error('[inquiry] id extraction failed', {
                _channelIdForInquiry: inquiryFor._channelIdForInquiry,
                inquiryFor_channel: inquiryFor.channel,
                selectedChannel_id: selectedChannel?._id,
                selectedChannel_type: typeof selectedChannel,
                inquiryFor_id: inquiryFor._id,
                inquiryFor_messageId: inquiryFor.messageId
            });
            setError('Datos del anuncio incompletos (id inválido)');
            return;
        }

        // Mostrar éxito al instante; la traducción ocurre en background en el backend.
        setInquirySuccess(true);
        setTimeout(() => { setInquiryFor(null); }, 800);

        try {
            await v2SendInquiry(channelIdForInquiry, messageId, {
                userId: currentUser._id,
                name: inquiryName.trim(),
                email: inquiryEmail.trim(),
                message: inquiryMsg.trim()
            });
        } catch (e) {
            // Si realmente falla, mostrarlo
            setInquirySuccess(false);
            setError(e.response?.data?.error || e.message);
        }
    };

    // ── Reacciones ──
    const handleReact = async (messageId, emoji) => {
        try {
            const { data } = await v2ReactToMessage(messageId, { emoji });
            const updater = (m) => String(m._id) === String(messageId) ? { ...m, reactions: data.reactions } : m;
            setMessages(prev => prev.map(updater));
            setFeed(prev => prev.map(updater));
            setReactionPicker(null);
            // Notifica al termómetro del Latido Dorado para refrescar su progreso.
            // Matcheamos por codepoint base para cubrir ❤️/❤ y ♥️/♥ (con o sin
            // VARIATION SELECTOR FE0F); usar includes() en un array de strings
            // fallaba si las formas Unicode no coincidían exactamente.
            const cp = emoji ? emoji.codePointAt(0) : 0;
            const isLikeEmoji = cp === 0x2764 || cp === 0x2665 || cp === 0x1F44D;
            if (isLikeEmoji) {
                window.dispatchEvent(new CustomEvent('latidoDorado:likeChanged', {
                    detail: { postId: String(messageId) }
                }));
            }
        } catch {}
    };

    const saveEditedPost = async () => {
        if (!editingPost) return;
        try {
            const { data } = await v2UpdateMessage(editingPost._id, {
                text: editingPost.originalText || '',
                visibility: editingPost.visibility,
                contactMode: editingPost.contactMode,
                commentsEnabled: !!editingPost.commentsEnabled,
                interests: editingPost.interests || []
            });
            const updater = (m) => String(m._id) === String(data.message._id)
                ? { ...m, originalText: data.message.originalText, text: data.message.originalText, visibility: data.message.visibility, contactMode: data.message.contactMode, commentsEnabled: data.message.commentsEnabled, interests: data.message.interests, hashtags: data.message.hashtags, translations: data.message.translations }
                : m;
            setMessages(prev => prev.map(updater));
            setFeed(prev => prev.map(updater));
            setEditingPost(null);
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const handleShare = async (m) => {
        try {
            const base = (process.env.REACT_APP_PUBLIC_URL || 'https://backend-dlmm.onrender.com').replace(/\/+$/, '');
            // URL amigable del post: /p/:messageId → landing con preview del post
            const url = `${base}/p/${String(m._id)}`;
            const title = m.channelName ? `${m.channelName} · KAIXO` : 'KAIXO';
            const text = m.text ? m.text.slice(0, 200) : (ct.shareDefault || 'Mira este Latido en KAIXO');

            // 1) Plugin Capacitor Share — abre el share sheet nativo de
            //    Android (elige app destino: WhatsApp, Telegram, SMS, ...).
            //    Es lo único que funciona en WebView Android; navigator.share
            //    no está disponible ahí.
            // 2) navigator.share — solo en navegadores modernos (web).
            // 3) clipboard — último fallback.
            let shared = false;
            try {
                const { Share } = await import('@capacitor/share');
                const can = await Share.canShare();
                if (can?.value) {
                    await Share.share({ title, text, url, dialogTitle: title });
                    shared = true;
                }
            } catch { /* plugin no disponible o cancelado */ }

            if (!shared && navigator.share) {
                try {
                    await navigator.share({ title, text, url });
                    shared = true;
                } catch (err) {
                    if (err?.name === 'AbortError') return;
                }
            }

            if (!shared && navigator.clipboard) {
                await navigator.clipboard.writeText(`${text}\n${url}`);
                setError(ct.linkCopied || 'Enlace copiado');
                setTimeout(() => setError(''), 2000);
            }

            try {
                const { data } = await v2IncrementShare(String(m._id));
                const updater = (x) => String(x._id) === String(m._id) ? { ...x, sharesCount: data.sharesCount } : x;
                setMessages(prev => prev.map(updater));
                setFeed(prev => prev.map(updater));
            } catch {}
        } catch (e) {
            // usuario canceló o sin soporte
        }
    };

    // ── Comentarios ──
    const openComments = async (messageId) => {
        try {
            const { data } = await v2GetComments(messageId);
            setCommentsFor({ messageId, comments: data.comments || [] });
            setCommentText('');
        } catch {}
    };

    // ── Comentarios inline (feed) ──
    const toggleInlineComments = async (messageId) => {
        const key = String(messageId);
        if (expandedComments[key]) {
            // Ya expandido → colapsar
            setExpandedComments(prev => {
                const next = { ...prev }; delete next[key]; return next;
            });
            return;
        }
        setExpandedComments(prev => ({ ...prev, [key]: 'loading' }));
        try {
            const { data } = await v2GetComments(messageId);
            setExpandedComments(prev => ({ ...prev, [key]: data.comments || [] }));
        } catch (e) {
            setExpandedComments(prev => ({ ...prev, [key]: [] }));
        }
    };

    const submitInlineComment = async (messageId) => {
        const key = String(messageId);
        const text = (inlineDraft[key] || '').trim();
        if (!text) return;
        try {
            const { data } = await v2AddComment(messageId, { text });
            setExpandedComments(prev => ({
                ...prev,
                [key]: [...(Array.isArray(prev[key]) ? prev[key] : []), data.comment]
            }));
            setInlineDraft(prev => ({ ...prev, [key]: '' }));
            const inc = (x) => String(x._id) === key ? { ...x, commentsCount: (x.commentsCount || 0) + 1 } : x;
            setFeed(prev => prev.map(inc));
            setMessages(prev => prev.map(inc));
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const deleteInlineComment = async (messageId, commentId) => {
        try {
            await v2DeleteComment(commentId);
            const key = String(messageId);
            setExpandedComments(prev => ({
                ...prev,
                [key]: (prev[key] || []).filter(c => String(c._id) !== String(commentId))
            }));
            const dec = (x) => String(x._id) === key ? { ...x, commentsCount: Math.max(0, (x.commentsCount || 0) - 1) } : x;
            setFeed(prev => prev.map(dec));
            setMessages(prev => prev.map(dec));
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    // Traducir comentario bajo demanda (toggle: si ya está traducido, lo oculta)
    const toggleTranslateComment = async (comment) => {
        const id = String(comment._id);
        const current = commentTranslations[id];
        if (current && current.text && !current.error) {
            // Toggle off: ocultar traducción y volver a original
            setCommentTranslations(prev => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            return;
        }
        setCommentTranslations(prev => ({ ...prev, [id]: { loading: true } }));
        try {
            const { data } = await v2TranslateComment(id);
            if (data.sameLanguage) {
                // Mismo idioma: no mostrar nada, limpiar
                setCommentTranslations(prev => {
                    const next = { ...prev };
                    delete next[id];
                    return next;
                });
            } else {
                setCommentTranslations(prev => ({
                    ...prev,
                    [id]: { text: data.text, language: data.language, loading: false }
                }));
            }
        } catch (e) {
            setCommentTranslations(prev => ({
                ...prev,
                [id]: { error: e.response?.data?.error || e.message, loading: false }
            }));
        }
    };

    // Like/unlike de un comentario
    const toggleLikeComment = async (comment) => {
        const id = String(comment._id);
        // Optimistic update
        const prevLocal = commentLikes[id];
        const baseCount = prevLocal ? prevLocal.likesCount : (comment.likesCount || 0);
        const baseLiked = prevLocal ? prevLocal.likedByMe : !!comment.likedByMe;
        const optimistic = {
            likesCount: baseLiked ? Math.max(0, baseCount - 1) : baseCount + 1,
            likedByMe: !baseLiked
        };
        setCommentLikes(prev => ({ ...prev, [id]: optimistic }));
        try {
            const { data } = await v2LikeComment(id);
            setCommentLikes(prev => ({
                ...prev,
                [id]: { likesCount: data.likesCount, likedByMe: data.likedByMe }
            }));
        } catch (e) {
            // Rollback
            setCommentLikes(prev => ({
                ...prev,
                [id]: { likesCount: baseCount, likedByMe: baseLiked }
            }));
            setError(e.response?.data?.error || e.message);
        }
    };

    const handleAddComment = async () => {
        if (!commentsFor || !commentText.trim()) return;
        try {
            const { data } = await v2AddComment(commentsFor.messageId, { text: commentText.trim() });
            setCommentsFor(prev => ({
                ...prev,
                comments: [...(prev?.comments || []), data.comment]
            }));
            const incCount = (x) => String(x._id) === String(commentsFor.messageId)
                ? { ...x, commentsCount: (x.commentsCount || 0) + 1 }
                : x;
            setMessages(prev => prev.map(incCount));
            setFeed(prev => prev.map(incCount));
            setCommentText('');
        } catch (e) { setError(e.response?.data?.error || e.message); }
    };

    const uid = String(currentUser?._id || '');
    const canPublish = selectedChannel && (
        String(selectedChannel.owner?._id || selectedChannel.owner) === uid ||
        (selectedChannel.admins || []).some(a => String(a._id || a) === uid)
    );
    const isOwner = selectedChannel && String(selectedChannel.owner?._id || selectedChannel.owner) === uid;

    // Modal del formulario de consulta (render fuera del modal principal)
    const InquiryModal = inquiryFor && (
        <div className="v2-inq-overlay" onClick={() => setInquiryFor(null)}>
            <div className="v2-inq-modal" onClick={e => e.stopPropagation()}>
                <h4>{ct.inquiryTitle}</h4>
                {inquirySuccess ? (
                    <p className="v2-inq-success">✓ {ct.inquirySent}</p>
                ) : (
                    <>
                        <div className="v2-inq-ro"><strong>{ct.inquiryAd}:</strong> {(inquiryFor.text || inquiryFor.originalText || '').slice(0, 80) || `[${inquiryFor.mediaType}]`}</div>
                        <div className="v2-inq-ro"><strong>{ct.inquiryPhone}:</strong> {currentUser.phone || '—'}</div>
                        <label>{ct.inquiryName}</label>
                        <input value={inquiryName} onChange={e => setInquiryName(e.target.value)} />
                        <label>{ct.inquiryEmail || 'Email'} <span className="v2-optional">({ct.optional || 'opcional'})</span></label>
                        <input type="email" value={inquiryEmail} onChange={e => setInquiryEmail(e.target.value)} />
                        <label>{ct.inquiryMessage}</label>
                        <textarea value={inquiryMsg} onChange={e => setInquiryMsg(e.target.value)} rows={4} maxLength={1000} />
                        <div className="v2-inq-actions">
                            <button className="v2-btn-secondary" onClick={() => setInquiryFor(null)}>
                                <Icons.Close size={14} /> {ct.close}
                            </button>
                            <button className="v2-btn-primary" onClick={submitInquiry} disabled={!inquiryMsg.trim()}>
                                <Icons.Send size={14} color="white" /> {ct.inquirySend}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    const CommentsModal = commentsFor && (
        <div className="v2-ig-comments-overlay" onClick={() => setCommentsFor(null)}>
            <div className="v2-ig-comments-sheet" onClick={e => e.stopPropagation()}>
                <div className="v2-ig-comments-header">
                    <div className="v2-ig-comments-grab" />
                    <h4>{ct.comments || 'Comentarios'}</h4>
                    <button
                        className="v2-ig-comments-close"
                        onClick={() => setCommentsFor(null)}
                        aria-label={ct.close || 'Cerrar'}
                    >
                        <Icons.Close size={18} />
                    </button>
                </div>
                <div className="v2-ig-comments-list">
                    {(commentsFor.comments || []).length === 0 && (
                        <p className="v2-center v2-dim" style={{ padding: '40px 20px' }}>{ct.noComments}</p>
                    )}
                    {(commentsFor.comments || []).map(c => {
                        const cid = String(c._id);
                        const tr = commentTranslations[cid];
                        const likeState = commentLikes[cid] || {
                            likesCount: c.likesCount || 0,
                            likedByMe: !!c.likedByMe
                        };
                        return (
                            <div key={c._id} className="v2-ig-comment">
                                <div className="v2-ig-comment-avatar">
                                    {c.author?.avatar
                                        ? <img src={c.author.avatar} alt="" />
                                        : <span>{(c.author?.name || '?')[0].toUpperCase()}</span>}
                                </div>
                                <div className="v2-ig-comment-body">
                                    <span
                                        className="v2-ig-comment-text"
                                        onClick={() => toggleTranslateComment(c)}
                                        title={ct.tapToTranslate || 'Toca para traducir'}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <strong>{c.author?.name || '—'}</strong> {c.text}
                                    </span>
                                    {tr?.loading && (
                                        <span className="v2-inline-comment-tr v2-dim">
                                            {ct.translating || 'Traduciendo…'}
                                        </span>
                                    )}
                                    {tr?.text && !tr.loading && (
                                        <span className="v2-inline-comment-tr">🌐 {tr.text}</span>
                                    )}
                                    <span className="v2-ig-comment-time">{new Date(c.createdAt).toLocaleString()}</span>
                                </div>
                                <button
                                    className={`v2-inline-comment-like ${likeState.likedByMe ? 'liked' : ''}`}
                                    onClick={() => toggleLikeComment(c)}
                                    aria-label="Me gusta"
                                >
                                    <span className="v2-inline-comment-like-icon">
                                        {likeState.likedByMe ? '❤️' : '🤍'}
                                    </span>
                                    {likeState.likesCount > 0 && (
                                        <span className="v2-inline-comment-like-count">{likeState.likesCount}</span>
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
                <div className="v2-ig-comment-input">
                    <input value={commentText} onChange={e => setCommentText(e.target.value)}
                        placeholder={ct.addComment} maxLength={500}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }} />
                    <button className="v2-ig-send-btn" onClick={handleAddComment} disabled={!commentText.trim()}>
                        {ct.publish || 'Publicar'}
                    </button>
                </div>
            </div>
        </div>
    );

    const profileOverlay = profileCode && (
        <ChannelProfile
            inviteCode={profileCode}
            currentUser={currentUser}
            onClose={() => setProfileCode(null)}
            onSubscribed={() => { loadEverything(); setProfileCode(null); }}
            onEdit={() => {
                const ch = channels.find(c => c.inviteCode === profileCode) || selectedChannel;
                if (ch) {
                    setEditingChannelId(ch._id);
                    setNewType(ch.type || 'personal');
                    setNewName(ch.name || '');
                    setNewDesc(ch.description || '');
                    setNewCategory(ch.category || '');
                    setNewBio(ch.bio || '');
                    setNewLinks((ch.links || []).join('\n'));
                    setNewIsPublic(ch.isPublic || false);
                    setNewContactMode(ch.contactMode || 'direct');
                    setNewTargetCountries(Array.isArray(ch.targetCountries) ? ch.targetCountries : []);
                    setNewTargetRegions(Array.isArray(ch.targetRegions) ? ch.targetRegions : []);
                    setNewTargetLanguages(Array.isArray(ch.targetLanguages) ? ch.targetLanguages : []);
                    setNewTargetInterests(Array.isArray(ch.targetInterests) ? ch.targetInterests : []);
                    setNewAvatarPreview(ch.avatar || null);
                    setNewAvatarFile(null);
                }
                setProfileCode(null);
                setView('createStep2');
            }}
        />
    );

    const EditPostModal = editingPost && (
        <div className="v2-inq-overlay" onClick={() => setEditingPost(null)}>
            <div className="v2-inq-modal" onClick={e => e.stopPropagation()}>
                <h4>{ct.editPost || 'Editar latido'}</h4>
                <label>{ct.postText || 'Texto'}</label>
                <textarea
                    rows={5}
                    maxLength={5000}
                    value={editingPost.originalText || ''}
                    onChange={e => setEditingPost(p => ({ ...p, originalText: e.target.value }))}
                />
                <label>{ct.visibility || 'Visibilidad'}</label>
                <select value={editingPost.visibility}
                    onChange={e => setEditingPost(p => ({ ...p, visibility: e.target.value }))}>
                    <option value="subscribers">{ct.visPublic || 'Público'}</option>
                    <option value="private">{ct.visPrivate || 'Privado'}</option>
                </select>
                {editingPost.isBusiness && (
                    <>
                        <label>{ct.contactMode || 'Modo de contacto'}</label>
                        <select value={editingPost.contactMode || ''}
                            onChange={e => setEditingPost(p => ({ ...p, contactMode: e.target.value }))}>
                            <option value="">{ct.cmDefault || 'Usar del canal'}</option>
                            <option value="none">{ct.cmNone || 'Ninguno'}</option>
                            <option value="direct">{ct.cmDirect || 'Chat directo'}</option>
                            <option value="form">{ct.cmForm || 'Formulario (CRM)'}</option>
                            <option value="both">{ct.cmBoth || 'Ambos'}</option>
                        </select>
                    </>
                )}
                <label style={{ marginTop: 8 }}>
                    <input type="checkbox" checked={!!editingPost.commentsEnabled}
                        onChange={e => setEditingPost(p => ({ ...p, commentsEnabled: e.target.checked }))} />
                    {' '}{ct.enableComments || 'Permitir comentarios'}
                </label>
                {editingPost.isBusiness && (
                    <>
                        <label style={{ marginTop: 10 }}>
                            {ct.postInterests || 'Intereses'} ({(editingPost.interests || []).length}/5)
                        </label>
                        <button
                            type="button"
                            onClick={() => setShowInterestPickerFor('editPost')}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e0e0e0', background: '#fff', cursor: 'pointer', fontSize: 14 }}
                        >
                            + {ct.addInterest || 'Seleccionar intereses'}
                        </button>
                        {(editingPost.interests || []).length > 0 && (
                            <div className="v2-chips-wrap" style={{ marginTop: 6 }}>
                                {editingPost.interests.map(k => (
                                    <button key={k} type="button" className="v2-chip active"
                                        onClick={() => setEditingPost(p => ({
                                            ...p,
                                            interests: (p.interests || []).filter(x => x !== k)
                                        }))}>
                                        {getInterestLabel(k, currentUser?.language || 'español')} ×
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}
                <div className="v2-inq-actions">
                    <button className="v2-btn-secondary" onClick={() => setEditingPost(null)}>
                        {ct.cancel || 'Cancelar'}
                    </button>
                    <button className="v2-btn-primary" onClick={saveEditedPost}>
                        {ct.save || 'Guardar'}
                    </button>
                </div>
            </div>
        </div>
    );

    const HashtagModal = (
        <HashtagView
            hashtagView={hashtagView}
            onClose={() => setHashtagView(null)}
            onOpenProfile={(code) => { setHashtagView(null); setProfileCode(code); }}
            onOpenImage={(url) => setFullImage(url)}
            onOpenVideo={(url) => setFullVideo(url)}
        />
    );

    // Contenido interior (tabs + vistas)
    const innerContent = (
        <>
            {error && <div className="v2-error">{error}</div>}

                {/* Header tabs: Latido + Leaderboard (la vieja pestaña "Mis latidos"
                    se eliminó; ahora la distinción vive en las sub-pestañas del home) */}
                {view === 'home' && (
                    <div className="v2-tabs">
                        <button className={`v2-tab active`} onClick={() => setView('home')}>
                            <span className="v2-heartbeat-icon"><Icons.Pulse size={16} /></span> {ct.tabHome}
                        </button>
                        <div className="v2-tabs-actions">
                            <button
                                className="v2-tab v2-tab-icon"
                                onClick={() => setShowLeaderboard(true)}
                                title={ct.fameTitle || 'Latido de la Fama'}
                                aria-label="Ranking"
                            >
                                <LeaderboardIcon size={18} />
                            </button>
                        </div>
                    </div>
                )}

                {/* VIEW: Home feed — feed mezclado completo con atajos arriba */}
                {view === 'home' && (() => {
                    // Contadores de CANALES únicos (para mostrar al lado de
                    // los botones-atajo).
                    const ownChannelsCount = (channels || []).filter(c => {
                        const ownerId = String(c.owner?._id || c.owner || '');
                        return ownerId === uid;
                    }).length;
                    const followedChannelsCount = (channels || []).filter(c => {
                        const ownerId = String(c.owner?._id || c.owner || '');
                        return ownerId !== uid;
                    }).length;
                    return (
                    <div className="v2-body">
                        {/* Atajos de navegación arriba del feed. NO filtran el
                            feed: abren pantallas distintas con las bandas. */}
                        <div className="v2-feed-shortcuts">
                            <button
                                className="v2-feed-shortcut-btn"
                                onClick={() => setView('followedChannels')}
                            >
                                <Icons.Users size={14} />
                                <span>{ct.filterFollowing || 'Siguiendo'}</span>
                                {followedChannelsCount > 0 && (
                                    <span className="v2-feed-filter-count">{followedChannelsCount}</span>
                                )}
                            </button>
                            <button
                                className="v2-feed-shortcut-btn"
                                onClick={() => setView('ownChannels')}
                            >
                                <Icons.User size={14} />
                                <span>{ct.filterOwn || 'Mis latidos'}</span>
                                {ownChannelsCount > 0 && (
                                    <span className="v2-feed-filter-count">{ownChannelsCount}</span>
                                )}
                            </button>
                            {ownChannelsCount > 0 && (
                                <button
                                    className="v2-feed-shortcut-btn"
                                    onClick={() => setView('followers')}
                                >
                                    <Icons.Heart size={14} />
                                    <span>{ct.followers || 'Seguidores'}</span>
                                </button>
                            )}
                        </div>

                        {/* Barra de historias activas (24h) de canales seguidos */}
                        <StoriesBar refreshKey={feed.length} />

                        {/* Feed mezclado completo: seguidos + propios + sponsored + descubrimiento */}
                        {loading ? <p className="v2-center">{ct.loading}</p>
                         : feed.length === 0 ? <p className="v2-center v2-dim">{ct.emptyFeed}</p>
                         : feed.map(m => (
                            <FeedItem
                                key={m._id}
                                message={m}
                                currentUserId={uid}
                                channels={channels}
                                expandedComments={expandedComments}
                                inlineDraft={inlineDraft}
                                commentTranslations={commentTranslations}
                                commentLikes={commentLikes}
                                reactionPickerId={reactionPicker}
                                onOpenProfile={(code) => setProfileCode(code)}
                                onOpenImage={(url) => setFullImage(url)}
                                onOpenVideo={(url) => setFullVideo(url)}
                                onReact={handleReact}
                                onToggleReactionPicker={(id) => setReactionPicker(reactionPicker === id ? null : id)}
                                onToggleComments={toggleInlineComments}
                                onShare={handleShare}
                                onDraftChange={(mid, v) => setInlineDraft(p => ({ ...p, [String(mid)]: v }))}
                                onSubmitComment={submitInlineComment}
                                onToggleTranslateComment={toggleTranslateComment}
                                onToggleLikeComment={toggleLikeComment}
                                onDeleteComment={deleteInlineComment}
                                onChatWithAdmin={handleChatWithAdmin}
                                onInquiry={openInquiry}
                            />
                        ))}
                    </div>
                    );
                })()}

                {/* VIEW: Canales que sigo — bandas, sin feed */}
                {view === 'followedChannels' && (
                    <FollowedChannelsList
                        channels={channels}
                        currentUserId={uid}
                        loading={loading}
                        onOpenChannel={openChannel}
                    />
                )}

                {/* VIEW: Mis canales propios — bandas + botón crear latido */}
                {view === 'ownChannels' && (
                    <OwnChannelsList
                        channels={channels}
                        currentUserId={uid}
                        loading={loading}
                        onCreateClick={() => { resetCreateForm(); setView('createStep1'); }}
                        onOpenProfile={(code) => setProfileCode(code)}
                        onOpenChannel={openChannel}
                    />
                )}

                {/* VIEW: Seguidores — usuarios que siguen cualquiera de mis canales */}
                {view === 'followers' && (
                    <FollowersList followers={followers} loading={followersLoading} />
                )}

                {/* VIEW: List (ya no se usa, se mantiene hasta limpiar rutas internas) */}
                {view === 'list' && (
                    <div className="v2-body">
                        <div className="v2-actions-row">
                            <button className="v2-btn-primary" onClick={() => { resetCreateForm(); setView('createStep1'); }}>
                                <Icons.Plus size={16} color="white" /> {ct.createBtn}
                            </button>
                        </div>
                        {loading ? <p className="v2-center">{ct.loading}</p>
                         : channels.length === 0 ? <p className="v2-center v2-dim">{ct.empty}</p>
                         : channels.map(c => {
                            return (
                            <div key={c._id} className="v2-channel-item" onClick={() => openChannel(c)}>
                                <div className="v2-channel-avatar">
                                    {c.avatar ? <img src={c.avatar} alt="" /> : c.name?.[0]}
                                </div>
                                <div className="v2-channel-info">
                                    <div className="v2-channel-name">{c.name}</div>
                                    <div className="v2-channel-preview">{c.status || c.description || '—'}</div>
                                </div>
                            </div>
                            );
                         })}
                    </div>
                )}

                {/* VIEW: Create Step 1 - Type */}
                {view === 'createStep1' && (
                    <div className="v2-body">
                        <h4 className="v2-wizard-q">{ct.chooseType}</h4>
                        <button className="v2-type-card" onClick={() => { setNewType('personal'); setView('createStep2'); }}>
                            <div className="v2-type-icon"><Icons.User size={28} /></div>
                            <div className="v2-type-text">
                                <div className="v2-type-title">{ct.typePersonal}</div>
                                <div className="v2-type-desc">{ct.typePersonalDesc}</div>
                            </div>
                        </button>
                        <button className="v2-type-card" onClick={() => { setNewType('business'); setView('createStep2'); }}>
                            <div className="v2-type-icon"><Icons.Store size={28} /></div>
                            <div className="v2-type-text">
                                <div className="v2-type-title">{ct.typeBusiness}</div>
                                <div className="v2-type-desc">{ct.typeBusinessDesc}</div>
                            </div>
                        </button>
                    </div>
                )}

                {/* VIEW: Create Step 2 - Details */}
                {view === 'createStep2' && (
                    <div className="v2-body">
                        <div className="v2-avatar-pick" onClick={() => avatarInputRef.current?.click()}>
                            {newAvatarPreview
                                ? <img src={newAvatarPreview} alt="" />
                                : <span className="v2-avatar-placeholder"><Icons.Camera size={24} /></span>
                            }
                            <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                                onChange={e => {
                                    const f = e.target.files?.[0];
                                    if (f) { setNewAvatarFile(f); setNewAvatarPreview(URL.createObjectURL(f)); }
                                }} />
                        </div>
                        <label>{ct.name}</label>
                        <input value={newName} onChange={e => setNewName(e.target.value)} maxLength={100} />
                        <label>{ct.description}</label>
                        <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} rows={2} maxLength={500} />
                        {newType === 'business' && (
                            <>
                                <label>{ct.category}</label>
                                <select value={newCategory} onChange={e => setNewCategory(e.target.value)}>
                                    <option value="">{ct.selectCategory}</option>
                                    {BUSINESS_CATEGORIES.map(key => (
                                        <option key={key} value={key}>{ct.categoryLabels?.[key] || key}</option>
                                    ))}
                                </select>
                                <label>{ct.bio}</label>
                                <textarea value={newBio} onChange={e => setNewBio(e.target.value)} rows={2} maxLength={300} />
                                <label>{ct.links}</label>
                                <textarea value={newLinks} onChange={e => setNewLinks(e.target.value)} rows={2} placeholder="ejemplo.com (sin www.)" />
                                <label style={{ marginTop: 8 }}>
                                    <input type="checkbox" checked={newIsPublic} onChange={e => setNewIsPublic(e.target.checked)} />
                                    {' '}{ct.isPublic}
                                </label>

                                <label style={{ marginTop: 12 }}>{ct.contactMode || '¿Cómo pueden contactarte los clientes?'}</label>
                                <select value={newContactMode} onChange={e => setNewContactMode(e.target.value)}>
                                    <option value="none">{ct.cmNone || 'Ninguno — solo leer'}</option>
                                    <option value="direct">{ct.cmDirect || 'Chat directo con el admin'}</option>
                                    <option value="form">{ct.cmForm || 'Formulario de consulta (va al CRM)'}</option>
                                    <option value="both">{ct.cmBoth || 'Ambos (chat + formulario)'}</option>
                                </select>

                                <div className="v2-targeting-block">
                                    <div className="v2-targeting-header">
                                        <strong>{ct.targeting || 'Público objetivo'}</strong>
                                        <span className="v2-dim v2-small">{ct.targetingHint || 'Deja vacío para llegar a todos tus suscriptores. Si rellenas, solo llegará a quienes coincidan.'}</span>
                                    </div>

                                    <label>{ct.targetCountries || 'Países'}</label>
                                    <select
                                        value=""
                                        onChange={e => {
                                            const v = e.target.value;
                                            if (!v) return;
                                            setNewTargetCountries(prev => prev.includes(v) ? prev : [...prev, v]);
                                        }}
                                    >
                                        <option value="">{ct.selectCountry || '+ Añadir país'}</option>
                                        {COUNTRIES_LIST.filter(c => !newTargetCountries.includes(c.id)).map(c => (
                                            <option key={c.id} value={c.id}>{c.native}</option>
                                        ))}
                                    </select>
                                    {newTargetCountries.length > 0 && (
                                        <div className="v2-chips-wrap" style={{ marginTop: 6 }}>
                                            {newTargetCountries.map(c => (
                                                <button
                                                    key={c}
                                                    type="button"
                                                    className="v2-chip active"
                                                    onClick={() => {
                                                        setNewTargetCountries(prev => prev.filter(x => x !== c));
                                                        // Limpiar regiones del país quitado
                                                        const regionsOfRemoved = getRegionsForCountry(c);
                                                        if (regionsOfRemoved.length) {
                                                            setNewTargetRegions(prev => prev.filter(r => !regionsOfRemoved.includes(r)));
                                                        }
                                                    }}
                                                >
                                                    {nativeName(c)} ×
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {newTargetCountries.length > 0 && (
                                        <>
                                            <label>{ct.targetRegions || 'Regiones'}</label>
                                            <select
                                                value=""
                                                onChange={e => {
                                                    const v = e.target.value;
                                                    if (!v) return;
                                                    setNewTargetRegions(prev => prev.includes(v) ? prev : [...prev, v]);
                                                }}
                                            >
                                                <option value="">{ct.selectRegion || '+ Añadir región'}</option>
                                                {newTargetCountries.flatMap(country =>
                                                    getRegionsForCountry(country).map(region => ({ country, region }))
                                                ).filter(({ region }) => !newTargetRegions.includes(region))
                                                 .map(({ country, region }) => (
                                                    <option key={`${country}|${region}`} value={region}>
                                                        {nativeName(country)}: {region}
                                                    </option>
                                                 ))}
                                            </select>
                                            {newTargetRegions.length > 0 && (
                                                <div className="v2-chips-wrap" style={{ marginTop: 6 }}>
                                                    {newTargetRegions.map(r => (
                                                        <button
                                                            key={r}
                                                            type="button"
                                                            className="v2-chip active"
                                                            onClick={() => setNewTargetRegions(prev => prev.filter(x => x !== r))}
                                                        >
                                                            {r} ×
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}

                                    <label>{ct.targetLanguages || 'Idiomas'}</label>
                                    <div className="v2-chips-wrap">
                                        {LANGUAGE_OPTIONS.map(lang => (
                                            <button
                                                key={lang}
                                                type="button"
                                                className={`v2-chip ${newTargetLanguages.includes(lang) ? 'active' : ''}`}
                                                onClick={() => setNewTargetLanguages(prev =>
                                                    prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]
                                                )}
                                            >
                                                {lang}
                                            </button>
                                        ))}
                                    </div>

                                    <label>{ct.targetInterests || 'Intereses'}</label>
                                    <button
                                        type="button"
                                        onClick={() => setShowInterestPickerFor('newTarget')}
                                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e0e0e0', background: '#fff', cursor: 'pointer', fontSize: 14 }}
                                    >
                                        + {ct.addInterest || 'Seleccionar intereses'}
                                    </button>
                                    {newTargetInterests.length > 0 && (
                                        <div className="v2-chips-wrap" style={{ marginTop: 6 }}>
                                            {newTargetInterests.map(key => (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    className="v2-chip active"
                                                    onClick={() => setNewTargetInterests(prev => prev.filter(i => i !== key))}
                                                >
                                                    {getInterestLabel(key, currentUser?.language || 'español')} ×
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        <button className="v2-btn-primary v2-btn-full" onClick={handleCreateOrUpdate}>
                            {editingChannelId ? (ct.save || 'Guardar') : ct.create}
                        </button>
                    </div>
                )}

                {/* VIEW: Subscribe */}
                {view === 'subscribe' && (
                    <div className="v2-body">
                        <div className="v2-search-wrap">
                            <Icons.Search size={16} />
                            <input
                                className="v2-search-input"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                placeholder={ct.searchPlaceholder || 'Buscar canales o @usuarios...'}
                                autoFocus
                            />
                        </div>
                        {searchLoading && <p className="v2-center v2-dim">{ct.loading}</p>}
                        {!searchLoading && searchQuery.length >= 2 && searchResults.length === 0 && (
                            <p className="v2-center v2-dim">{ct.noResults || 'Sin resultados'}</p>
                        )}
                        <div className="v2-search-results">
                            {searchResults.map(c => (
                                <div key={c._id} className="v2-search-item" onClick={() => setProfileCode(c.handle ? `@${c.handle}` : c.inviteCode)}>
                                    <div className="v2-search-avatar">
                                        {c.avatar ? <img src={c.avatar} alt="" /> : (c.name?.[0] || '?').toUpperCase()}
                                    </div>
                                    <div className="v2-search-meta">
                                        <strong>{c.name}</strong>
                                        <span className="v2-dim v2-small">
                                            {c.handle ? `@${c.handle}` : ''} · {c.subscribersCount} {ct.subscribers || 'seguidores'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* VIEW: Chat (canal individual) */}
                {view === 'chat' && selectedChannel && (() => {
                    const imSubscribed = (selectedChannel.subscribers || [])
                        .some(s => String(s._id || s) === uid);
                    const handleSubscribeHere = async () => {
                        try {
                            await v2Subscribe({
                                userId: currentUser._id,
                                inviteCode: selectedChannel.inviteCode
                            });
                            await loadEverything();
                            // Re-cargar selectedChannel para que el flag cambie
                            try {
                                const { data } = await v2GetChannel(selectedChannel._id);
                                setSelectedChannel(data.channel);
                            } catch {}
                        } catch (e) {
                            setError(e.response?.data?.error || e.message);
                        }
                    };
                    return (
                    <div className="v2-body v2-chat">
                        <div className="v2-chat-header">
                            <span className="v2-sub-count">
                                <Icons.Users size={14} /> {(selectedChannel.subscribers || []).length} {ct.subscribers}
                            </span>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button className="v2-link-btn" onClick={() => setProfileCode(selectedChannel.inviteCode)}>
                                    <Icons.Info size={14} color="white" /> {ct.viewProfile}
                                </button>
                            </div>
                        </div>

                        {/* CTA arriba de la primera publicación: Invitar (owner) o Seguir (visitante) */}
                        <div className="v2-channel-cta">
                            {isOwner ? (
                                <button
                                    className="v2-btn-primary v2-btn-full"
                                    onClick={() => setShowInviteFor(selectedChannel)}
                                >
                                    <Icons.Users size={16} color="white" /> {ct.inviteFollowers || 'Invitar a seguir'}
                                </button>
                            ) : imSubscribed ? (
                                <button className="v2-btn-primary v2-btn-full" disabled>
                                    <Icons.Check size={16} color="white" /> {ct.alreadySubscribed}
                                </button>
                            ) : (
                                <button
                                    className="v2-btn-primary v2-btn-full"
                                    onClick={handleSubscribeHere}
                                >
                                    <Icons.Plus size={16} color="white" /> {ct.subscribeCta}
                                </button>
                            )}
                        </div>

                        <div className="v2-messages">
                            {messages.length === 0 ? <p className="v2-center v2-dim">{ct.noMessages}</p>
                             : messages.map(m => (
                                <ChatMessageItem
                                    key={m._id || m.messageId}
                                    message={m}
                                    channel={selectedChannel}
                                    currentUserId={uid}
                                    isOwner={isOwner}
                                    reactionPickerId={reactionPicker}
                                    onOpenImage={(url) => setFullImage(url)}
                                    onOpenVideo={(url) => setFullVideo(url)}
                                    onOpenChannelAvatar={() => { if (selectedChannel?.avatar) setFullImage(selectedChannel.avatar); }}
                                    onReact={handleReact}
                                    onToggleReactionPicker={(id) => setReactionPicker(reactionPicker === id ? null : id)}
                                    onOpenComments={openComments}
                                    onShare={handleShare}
                                    onEdit={setEditingPost}
                                    onBoost={setBoostFor}
                                    onChatWithAdmin={handleChatWithAdmin}
                                    onInquiry={openInquiry}
                                />
                             ))}
                            <div ref={feedEndRef} />
                        </div>

                        {canPublish ? (
                            <div className="v2-publish">
                                {pendingImagePreview && (
                                    <div className="v2-pending-image">
                                        <img src={pendingImagePreview} alt="preview" />
                                        <button className="v2-pending-remove" onClick={clearPendingImage} aria-label={ct.close}>
                                            <Icons.Close size={14} color="white" />
                                        </button>
                                    </div>
                                )}
                                {pendingMedia.length > 0 && (
                                    <>
                                        <div className="v2-pending-carousel">
                                            {pendingMedia.map((m, i) => (
                                                <div key={i} className="v2-pending-item">
                                                    {m.type === 'video'
                                                        ? <video src={m.previewUrl} muted playsInline />
                                                        : <img src={m.previewUrl} alt="" />}
                                                    <button
                                                        className="v2-pending-remove"
                                                        onClick={() => removePendingMedia(i)}
                                                        aria-label={ct.close}
                                                    >
                                                        <Icons.Close size={12} color="white" />
                                                    </button>
                                                </div>
                                            ))}
                                            <span className="v2-pending-count">{pendingMedia.length} / 10</span>
                                        </div>
                                        <div className="v2-media-hint">
                                            💡 Para que se vean sin recortes ni barras negras usa <strong>vertical 9:16</strong> (ideal 1080×1920) para fotos y videos. Horizontales también funcionan pero se verán con bordes en el feed.
                                        </div>
                                    </>
                                )}
                                <div className="v2-publish-meta">
                                    <div className="v2-pill">
                                        <span className="v2-pill-label">{ct.visibility}</span>
                                        <select className="v2-pill-select" value={visibility} onChange={e => setVisibility(e.target.value)}>
                                            <option value="subscribers">{ct.visPublic || 'Público'}</option>
                                            <option value="private">{ct.visPrivate || 'Privado'}</option>
                                        </select>
                                    </div>
                                    <div className="v2-pill" onClick={() => setCommentsEnabled(!commentsEnabled)} style={{ cursor: 'pointer' }}>
                                        <span className="v2-pill-label">{ct.comments}</span>
                                        <span style={{ color: commentsEnabled ? 'var(--orange)' : 'var(--text-secondary)' }}>
                                            {commentsEnabled ? (ct.commentsOn || 'Sí') : (ct.commentsOff || 'No')}
                                        </span>
                                    </div>
                                    {selectedChannel?.type === 'business' && (
                                        <div className="v2-pill">
                                            <span className="v2-pill-label">{ct.contactModeShort || 'Contacto'}</span>
                                            <select className="v2-pill-select" value={overrideContactMode}
                                                onChange={e => setOverrideContactMode(e.target.value)}>
                                                <option value="">{ct.cmDefault || 'Usar del canal'}</option>
                                                <option value="none">{ct.cmNone || 'Ninguno'}</option>
                                                <option value="direct">{ct.cmDirect || 'Chat directo'}</option>
                                                <option value="form">{ct.cmForm || 'Formulario (CRM)'}</option>
                                                <option value="both">{ct.cmBoth || 'Ambos'}</option>
                                            </select>
                                        </div>
                                    )}
                                </div>
                                {selectedChannel?.type === 'business' && (
                                    <div className="v2-post-interests">
                                        <div className="v2-post-interests-header">
                                            <strong>{ct.postInterests || 'Intereses de este latido'} ({postInterests.length}/5)</strong>
                                            <span className="v2-dim v2-small">
                                                {ct.postInterestsHint || 'Selecciona 3-5 para alimentar el algoritmo de descubrimiento.'}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowInterestPickerFor('post')}
                                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e0e0e0', background: '#fff', cursor: 'pointer', fontSize: 14 }}
                                        >
                                            + {ct.addInterest || 'Seleccionar intereses (3-5)'}
                                        </button>
                                        {postInterests.length > 0 && (
                                            <div className="v2-chips-wrap" style={{ marginTop: 6 }}>
                                                {postInterests.map(k => (
                                                    <button key={k} type="button" className="v2-chip active"
                                                        onClick={() => setPostInterests(p => p.filter(x => x !== k))}>
                                                        {getInterestLabel(k, currentUser?.language || 'español')} ×
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="v2-publish-row">
                                    <input
                                        type="file"
                                        accept="image/*,video/*"
                                        multiple
                                        ref={imageInputRef}
                                        onChange={handleMediaPick}
                                        style={{ display: 'none' }}
                                    />
                                    <button className="v2-icon-btn-alt" title={ct.attachImage}
                                        onClick={() => imageInputRef.current?.click()}
                                        disabled={uploading || pendingMedia.length >= 10}
                                        aria-label={ct.attachImage}>
                                        <Icons.ImageOutline size={18} />
                                    </button>
                                    <textarea
                                        value={text} onChange={e => setText(e.target.value)}
                                        placeholder={uploading ? ct.loading : ct.publishPlaceholder}
                                        rows={2} disabled={uploading} maxLength={5000}
                                    />
                                    <div className="v2-publish-actions">
                                        <button
                                            className="v2-btn-primary"
                                            onClick={() => { setIsStory(false); setTimeout(handlePublish, 0); }}
                                            disabled={(!text.trim() && !pendingImage && pendingMedia.length === 0) || uploading}
                                            title="Publica en el feed permanente"
                                        >
                                            {ct.publishLatido || 'Publicar latido'}
                                        </button>
                                        <button
                                            className="v2-btn-secondary v2-btn-story"
                                            onClick={() => { setIsStory(true); setTimeout(handlePublish, 0); }}
                                            disabled={(!pendingImage && pendingMedia.length === 0) || uploading}
                                            title="Aparece en la barra de historias y desaparece en 24 horas"
                                        >
                                            {ct.publishStory || 'Publicar historia'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="v2-readonly">{ct.onlyAdminsCanPost}</div>
                        )}

                        <div className="v2-chat-actions">
                            {isOwner ? (
                                <button className="v2-btn-danger" onClick={handleDelete}>{ct.delete}</button>
                            ) : (
                                <button className="v2-btn-danger" onClick={handleUnsubscribe}>{ct.leave}</button>
                            )}
                        </div>
                    </div>
                    );
                })()}
        </>
    );

    // Modo embebido: sin overlay, sin portal, sin header
    if (embedded) {
        return (
            <div className="v2-embedded">
                <ImageViewer src={fullImage} onClose={() => setFullImage(null)} />
                <VideoViewer src={fullVideo} onClose={() => setFullVideo(null)} />
                {publishModal && (
                    <PublishModal
                        channel={publishModal.channel}
                        mode={publishModal.mode}
                        currentUser={currentUser}
                        onClose={() => setPublishModal(null)}
                        onPublished={(newMsg) => {
                            // Si fue latido → lo metemos al feed local (al tope).
                            // Si fue historia → la StoriesBar se recarga al re-abrir.
                            if (newMsg && !newMsg.isStory) {
                                setFeed(prev => {
                                    const exists = prev.some(m => String(m._id) === String(newMsg._id));
                                    if (exists) return prev;
                                    return [{ ...newMsg, channelName: publishModal.channel.name, channelAvatar: publishModal.channel.avatar, channelOwner: publishModal.channel.owner, channelInviteCode: publishModal.channel.inviteCode }, ...prev];
                                });
                            }
                        }}
                    />
                )}
                {InquiryModal}
                {CommentsModal}
                {profileOverlay}
                {HashtagModal}
                {EditPostModal}
                {showInviteFor && (
                    <InviteFollowersModal
                        channelId={showInviteFor._id}
                        channelName={showInviteFor.name}
                        currentUserId={currentUser?._id}
                        onClose={() => setShowInviteFor(null)}
                    />
                )}
                {boostFor && (
                    <BoostCreateModal
                        message={boostFor}
                        channel={selectedChannel}
                        currentUser={currentUser}
                        onClose={() => setBoostFor(null)}
                        onCreated={() => setError('')}
                    />
                )}
                {showLeaderboard && (
                    <LeaderboardModal
                        currentUser={currentUser}
                        onClose={() => setShowLeaderboard(false)}
                        onSelectCreator={(c) => {
                            setShowLeaderboard(false);
                            // Redirige al perfil público del creador si tiene handle
                            if (c.handle) {
                                window.dispatchEvent(new CustomEvent('open_channel_profile', { detail: { inviteCode: '@' + c.handle } }));
                            }
                        }}
                    />
                )}
                {/* Sub-header solo para vistas internas (crear, suscribir, chat, listas de bandas) */}
                {!['home', 'list'].includes(view) && (
                    <div className="v2-subheader">
                        <button className="v2-icon-btn" onClick={() => {
                            if (view === 'chat') { setView('home'); setSelectedChannel(null); }
                            else if (view === 'createStep2') setView('createStep1');
                            else setView('home');
                        }}>
                            <Icons.ArrowBack size={18} color="var(--text-primary)" />
                        </button>
                        <span className="v2-subheader-title">
                            {view === 'chat' ? selectedChannel?.name :
                             view.startsWith('create') ? ct.createBtn :
                             view === 'subscribe' ? ct.subscribeBtn :
                             view === 'followedChannels' ? (ct.filterFollowing || 'Siguiendo') :
                             view === 'ownChannels' ? (ct.filterOwn || 'Mis latidos') :
                             view === 'followers' ? (ct.followers || 'Seguidores') :
                             ct.title}
                        </span>
                    </div>
                )}
                {innerContent}
            </div>
        );
    }

    // Modo modal: con overlay + portal (para compatibilidad con links /c/<code>)
    return ReactDOM.createPortal(
        <div className="v2-overlay" onClick={onClose}>
            {InquiryModal}
            {CommentsModal}
            {profileOverlay}
            {showInviteFor && (
                <InviteFollowersModal
                    channelId={showInviteFor._id}
                    channelName={showInviteFor.name}
                    currentUserId={currentUser?._id}
                    onClose={() => setShowInviteFor(null)}
                />
            )}
            <ImageViewer src={fullImage} onClose={() => setFullImage(null)} />
            <VideoViewer src={fullVideo} onClose={() => setFullVideo(null)} />
            <div className="v2-modal" onClick={(e) => e.stopPropagation()}>
                <div className="v2-header">
                    <button className="v2-icon-btn" onClick={() => {
                        if (view === 'chat') { setView('home'); setSelectedChannel(null); }
                        else if (view === 'createStep2') setView('createStep1');
                        else if (['createStep1', 'subscribe'].includes(view)) setView('home');
                        else if (['followedChannels', 'ownChannels'].includes(view)) setView('home');
                        else onClose();
                    }} aria-label={ct.back}>
                        <Icons.ArrowBack size={20} color="white" />
                    </button>
                    <h3>
                        {view === 'chat' ? selectedChannel?.name :
                         view.startsWith('create') ? ct.createBtn :
                         view === 'subscribe' ? ct.subscribeBtn :
                         <><span className="v2-heartbeat-icon"><Icons.Pulse size={18} /></span> {ct.title}</>}
                    </h3>
                    <button className="v2-icon-btn" onClick={onClose} aria-label={ct.close}>
                        <Icons.Close size={18} color="white" />
                    </button>
                </div>
                {innerContent}
            </div>
            {showInterestPickerFor === 'newTarget' && (
                <InterestPickerModal
                    value={newTargetInterests}
                    max={20}
                    language={currentUser?.language || 'español'}
                    accent="#ff6b35"
                    onSave={(next) => setNewTargetInterests(next)}
                    onClose={() => setShowInterestPickerFor(null)}
                />
            )}
            {showInterestPickerFor === 'post' && (
                <InterestPickerModal
                    value={postInterests}
                    max={5}
                    language={currentUser?.language || 'español'}
                    accent="#ff6b35"
                    onSave={(next) => setPostInterests(next)}
                    onClose={() => setShowInterestPickerFor(null)}
                />
            )}
            {showInterestPickerFor === 'editPost' && editingPost && (
                <InterestPickerModal
                    value={editingPost.interests || []}
                    max={5}
                    language={currentUser?.language || 'español'}
                    accent="#ff6b35"
                    onSave={(next) => setEditingPost(p => ({ ...p, interests: next }))}
                    onClose={() => setShowInterestPickerFor(null)}
                />
            )}
        </div>,
    document.body);
}

export default ChannelsV2Modal;
